<template>
    <div id="barcode-status-appointment">
        <!-- Operator Page Header -->
        <header class="header-operator">
            <span class="header__back" @click="redirectPage('operator/orders')">
                <BackIcon />
            </span>
            <span class="header__title">
                Leitura de Status
            </span>
            <button type="button" class="header__action" @click="goToBarcodePrint">
                Configurar e imprimir
            </button>
        </header>

        <div class="body-content">
            <!-- Metrics Cards -->
            <section class="info-card">
            <h2 id="infoTitle"><span aria-hidden="true">ⓘ</span> Informações da sessão</h2>
            <div class="metrics">
                <div>
                    <span>LEITURAS</span>
                    <strong id="totalReadings">{{ state.readings.length }}</strong>
                </div>
                <div>
                    <span>ÚLTIMA ORDEM</span>
                    <strong id="lastOrder">{{ state.readings[0] ? state.readings[0].order : (state.activeContext ?
                        state.activeContext.order : '-') }}</strong>
                </div>
                <div>
                    <span>STATUS ATUAL</span>
                    <strong id="currentStatus" :style="{ color: state.readings[0] ? STATUS[state.readings[0].statusCode].color : (state.activeContext && state.activeContext.status_color ? state.activeContext.status_color : '#738091') }">
                        {{ state.readings[0] ? STATUS[state.readings[0].statusCode].label : (state.activeContext && state.activeContext.status_name ? state.activeContext.status_name : 'Aguardando') }}
                    </strong>
                </div>
                <div>
                    <span>PARADAS</span>
                    <strong id="stopCount">{{ getStopCount() }}</strong>
                </div>
                <div>
                    <span>PEÇAS PRODUZIDAS</span>
                    <strong id="producedQuantity">{{ getProducedQuantity() }}</strong>
                </div>
            </div>
        </section>

        <!-- Main Workspace -->
        <div class="workspace" @click="handleWorkspaceClick">
            <!-- Left Side: Scanner and Config -->
            <aside class="scanner-card">
                <div class="scanner-illustration" aria-hidden="true">
                    <i></i><i></i><i></i><i></i><i></i>
                </div>
                <h2>Aguardando leitura...</h2>
                <p>Formatos Code 128: <strong>STATUS;000004</strong> ou <strong>ORDEM;OPERAÇÃO;QTDE</strong><br />Ex.: <code>STATUS;000004</code> ou
                    <code>A001;10;50</code></p>

                <!-- Active Order Context -->
                <div class="active-context">
                    <div>
                        <span>ORDEM ATIVA</span>
                        <strong>{{ state.activeContext ? state.activeContext.order : 'Nenhuma' }}</strong>
                    </div>
                    <div>
                        <span>OPERAÇÃO</span>
                        <strong>{{ state.activeContext ? state.activeContext.operation : 'Nenhuma' }}</strong>
                    </div>
                    <div v-if="state.activeContext && state.activeContext.planned_quantity">
                        <span>QUANTIDADE (PROD / PLAN)</span>
                        <strong>{{ `${state.activeContext.produced_quantity || 0} / ${state.activeContext.planned_quantity}` }}</strong>
                    </div>
                </div>

                <!-- Scan Input Header & Row -->
                <div class="input-header">
                    <label for="scanInput">Código lido</label>
                    <span
                        :class="['scanner-status-pill', isLoading ? 'processing' : (isInputFocused ? 'active' : 'inactive')]"
                        @mousedown.prevent
                        @click="focusInput"
                        title="Clique para focar o leitor"
                    >
                        {{ isLoading ? '● Processando...' : (isInputFocused ? '● Leitor Pronto' : '○ Clique para focar') }}
                    </span>
                </div>
                <div class="input-row">
                    <input :key="inputKey" id="scanInput" ref="scanInput" v-model="scanInput" type="text"
                        placeholder="STATUS;000004 ou A001;10;50" :readonly="isLoading" :aria-busy="isLoading"
                        @keydown="onScanKeydown" @focus="onScanInputFocus" @blur="onScanInputBlur" autocomplete="off" />
                    <button class="primary-button" @mousedown.prevent @click="processInput" :disabled="isLoading">
                        <span v-if="isLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Processar
                    </button>
                </div>
                <div :class="['feedback', feedback.type]" role="status" aria-live="polite">
                    {{ feedback.message }}
                </div>

                <!-- Quantity Config Summary -->
                <div class="quantity-config-summary">
                    <div class="quantity-config-summary__info">
                        <span class="quantity-config-summary__label">Qtde. produção:</span>
                        <strong class="quantity-config-summary__value">
                            {{ useDefaultQuantity ? `${defaultQuantity || 0} peças (auto)` : 'Manual (por leitura)' }}
                        </strong>
                    </div>
                    <button type="button" class="quantity-config-summary__btn" @mousedown.prevent @click="openConfigModal" title="Configurar quantidade">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="3"></circle>
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                        </svg>
                    </button>
                </div>

                <!-- Simulation Tools -->
                <div class="simulator">
                    <h3>Simular leitura</h3>
                    <p>Use os botões abaixo para testar cada status.</p>
                    <div class="status-buttons">
                        <button @mousedown.prevent @click="simulateStatus('2')" class="status setup" :disabled="isLoading">Setup <small>ST/2</small></button>
                        <button @mousedown.prevent @click="simulateStatus('3')" class="status started" :disabled="isLoading">Iniciado <small>ST/3</small></button>
                        <button @mousedown.prevent @click="simulateStatus('4')" class="status production" :disabled="isLoading">Produção
                            <small>ST/4</small></button>
                        <button @mousedown.prevent @click="simulateStatus('5')" class="status finished" :disabled="isLoading">Finalizado
                            <small>ST/5</small></button>
                        <button @mousedown.prevent @click="simulateStatus('99')" class="status stopped" :disabled="isLoading">Parada <small>ST/99</small></button>
                    </div>
                </div>
            </aside>

            <!-- Right Side: Session Log History -->
            <section class="history-panel">
                <div class="history-heading">
                    <div>
                        <p class="eyebrow">HISTÓRICO DE LEITURAS</p>
                        <h2>Eventos confirmados</h2>
                    </div>
                    <span class="count-pill">{{ state.readings.length }} registros</span>
                </div>
                <div class="history-list">
                    <div v-if="state.readings.length === 0" class="empty-state">
                        <div class="empty-icon" aria-hidden="true">⌁</div>
                        <strong>Nenhuma leitura registrada</strong>
                        <span>Simule um status ou use o campo do leitor.</span>
                    </div>
                    <article v-for="item in state.readings" :key="item.id" class="history-item"
                        :style="`--status-color:${STATUS[item.statusCode] ? STATUS[item.statusCode].color : '#738091'}`">
                        <div class="history-badge">{{ STATUS[item.statusCode] ? STATUS[item.statusCode].label : 'Apontamento' }}</div>
                        <div class="history-main">
                            <strong>{{ item.order }}</strong>
                            <span>{{ item.operation }} · {{ getEventDetail(item) }}</span>
                        </div>
                        <div class="history-time">
                            <strong>{{ STATUS[item.statusCode] ? STATUS[item.statusCode].label : '' }}</strong>
                            {{ formatTime(item.timestamp) }}
                        </div>
                        <button type="button" class="delete-reading-btn" @mousedown.prevent @click.stop="deleteReading(item)" title="Excluir apontamento">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                        </button>
                    </article>
                </div>
            </section>
        </div>
        </div>

        <!-- Quantity Modal (Status 4) -->
        <div class="custom-modal-backdrop" v-if="!quantityModalHidden" @click.self="closeQuantityModal">
            <section class="custom-modal production-custom-modal" role="dialog" aria-modal="true">
                <div class="modal-accent"></div>
                <div class="modal-header">
                    <div class="quantity-symbol" aria-hidden="true">50</div>
                    <div>
                        <span class="modal-kicker">STATUS 4</span>
                        <h2>Informar quantidade produzida</h2>
                    </div>
                    <button class="close-button" @click="closeQuantityModal" aria-label="Cancelar produção">×</button>
                </div>
                <p>A quantidade padrão está desativada. Informe quantas peças foram produzidas para concluir a leitura.
                </p>
                <div class="order-summary">
                    <span>Ordem identificada</span>
                    <strong>{{ state.pendingProduction ? state.pendingProduction.order : '-' }}</strong>
                </div>
                <form @submit.prevent="submitQuantity">
                    <label for="productionQuantity">Quantidade produzida <b style="color: #e5252a;">*</b></label>
                    <div class="quantity-input modal-quantity-input">
                        <input id="productionQuantity" type="number" min="1" step="1"
                            v-model.number="productionQuantity" ref="productionQuantityInput" required />
                        <span>peças</span>
                    </div>
                    <p class="field-error" v-if="productionQuantityError">
                        Informe uma quantidade inteira maior que zero.
                    </p>
                    <div class="modal-actions">
                        <button type="button" class="secondary-button" @click="closeQuantityModal">Cancelar</button>
                        <button type="submit" class="production-button">Registrar produção</button>
                    </div>
                </form>
            </section>
        </div>

        <!-- Stop Modal (Status 99) -->
        <div class="custom-modal-backdrop" v-if="!stopModalHidden" @click.self="closeStopModal">
            <section class="custom-modal" role="dialog" aria-modal="true">
                <div class="modal-accent"></div>
                <div class="modal-header">
                    <div class="stop-symbol" aria-hidden="true">Ⅱ</div>
                    <div>
                        <span class="modal-kicker">STATUS 99</span>
                        <h2>Informar motivo da parada</h2>
                    </div>
                    <button class="close-button" @click="closeStopModal" aria-label="Cancelar parada">×</button>
                </div>
                <p>A leitura de parada só será gravada depois que o operador informar o motivo.</p>
                <div class="order-summary">
                    <span>Ordem identificada</span>
                    <strong>{{ state.pendingStop ? state.pendingStop.order : '-' }}</strong>
                </div>
                <form @submit.prevent="submitStop">
                    <label for="stopReason">Motivo da parada <b style="color: #e5252a;">*</b></label>
                    <select id="stopReason" v-model="stopReason" required>
                        <option value="">Selecione o motivo</option>
                        <option v-for="reason in rejectionReasons" :key="reason.value" :value="reason.value">
                            {{ reason.text }}
                        </option>
                        <option value="Outro">Outro</option>
                    </select>
                    <p class="field-error" v-if="reasonError">Selecione um motivo para continuar.</p>

                    <div v-if="stopReason === 'Outro'" class="mt-3">
                        <label for="otherReason">Descreva o motivo <b style="color: #e5252a;">*</b></label>
                        <input id="otherReason" type="text" maxlength="120" v-model="otherReason"
                            placeholder="Informe o motivo da parada" ref="otherReasonInput" required />
                        <p class="field-error" v-if="otherReasonError">Descreva o motivo da parada.</p>
                    </div>

                    <label for="stopNote" class="mt-3">Observação <span>(opcional)</span></label>
                    <textarea id="stopNote" rows="3" maxlength="240" v-model="stopNote"
                        placeholder="Detalhes adicionais para o histórico"></textarea>

                    <div class="modal-actions">
                        <button type="button" class="secondary-button" @click="closeStopModal">Cancelar</button>
                        <button type="submit" class="danger-button">Confirmar parada</button>
                    </div>
                </form>
            </section>
        </div>

        <!-- Quantity Config Modal -->
        <div class="custom-modal-backdrop" v-if="configModalOpen" @click.self="closeConfigModal">
            <section class="custom-modal config-custom-modal" role="dialog" aria-modal="true">
                <div class="modal-accent config-accent"></div>
                <div class="modal-header">
                    <div class="config-symbol" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="3"></circle>
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                        </svg>
                    </div>
                    <div>
                        <span class="modal-kicker config-kicker">CONFIGURAÇÃO</span>
                        <h2>Quantidade da produção</h2>
                    </div>
                    <button class="close-button" @click="closeConfigModal" aria-label="Fechar configurações">×</button>
                </div>
                <p>Defina se a quantidade de peças será preenchida automaticamente ou informada manualmente a cada leitura do status 4 (Produção).</p>

                <div class="config-modal-body">
                    <div class="quantity-config-heading">
                        <div>
                            <h3>Usar quantidade padrão</h3>
                            <p class="config-desc">Quando ativado, a quantidade abaixo será aplicada automaticamente.</p>
                        </div>
                        <label class="switch" aria-label="Usar quantidade padrão">
                            <input type="checkbox" v-model="useDefaultQuantity" />
                            <span></span>
                        </label>
                    </div>
                    <label for="configDefaultQuantity">Quantidade padrão</label>
                    <div class="quantity-input">
                        <input id="configDefaultQuantity" type="number" min="1" step="1" v-model.number="defaultQuantity"
                            :disabled="!useDefaultQuantity" ref="configQuantityInput" />
                        <span>peças</span>
                    </div>
                    <div class="config-hint">
                        {{ useDefaultQuantity
                            ? `Produções serão registradas automaticamente com ${defaultQuantity || 0} peças.`
                            : 'Ao ler Produção (4), será aberta uma tela para informar a quantidade.'
                        }}
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="primary-button" @click="closeConfigModal">Fechar</button>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
    components: {
        BackIcon: () => import("@/assets/images/icons/back.svg")
    },
    computed: {
        ...mapGetters("app", [
            "flagShowOverlay",
            "flagShowModalPin"
        ])
    },
    watch: {
        $route() {
            this.remountInput();
            this.scheduleFocusRestore();
        },
        // When the global overlay hides, remount input and restore focus
        flagShowOverlay(newVal) {
            if (!newVal) {
                this.remountInput();
                this.scheduleFocusRestore();
                setTimeout(() => this.remountInput(), 400);
                setTimeout(() => this.remountInput(), 900);
            }
        },
        // When the PIN modal closes, remount input and restore focus
        flagShowModalPin(newVal) {
            if (!newVal) {
                this.remountInput();
                this.scheduleFocusRestore();
                setTimeout(() => this.remountInput(), 400);
                setTimeout(() => this.remountInput(), 900);
            }
        },
        scanInput(value) {
            if (!value || !/[çÇ]/.test(value)) return;
            this.scanInput = value.replace(/[çÇ]/g, ";");
        }
    },
    data() {
        return {
            prefix: localStorage.getItem("prefix") || this.$route.params.prefix,
            currentSite: this.$cookies.get("userInfo")?.currentSite || "",
            scanInput: "",
            inputKey: 0,
            isLoading: false,
            isInputFocused: false,
            feedback: {
                message: "O foco permanece no campo para receber o leitor.",
                type: ""
            },
            state: {
                readings: [],
                pendingStop: null,
                pendingProduction: null,
                activeContext: null
            },
            useDefaultQuantity: true,
            defaultQuantity: 50,
            productionQuantity: "",
            productionQuantityError: false,
            stopReason: "",
            otherReason: "",
            reasonError: false,
            otherReasonError: false,
            stopNote: "",
            stopModalHidden: true,
            quantityModalHidden: true,
            configModalOpen: false,
            statusList: [],
            rejectionReasons: [],
            STATUS: {
                "1": { label: "Não Iniciado", className: "not-started", color: "#61b3fa" },
                "2": { label: "Setup", className: "setup", color: "#737d8d" },
                "3": { label: "Iniciado", className: "started", color: "#2864dc" },
                "4": { label: "Produção", className: "production", color: "#16a34a" },
                "5": { label: "Finalizado", className: "finished", color: "#7c3aed" },
                "99": { label: "Parada", className: "stopped", color: "#e5252a" }
            }
        };
    },
    activated() {
        this.remountInput();
        this.scheduleFocusRestore();
    },
    async mounted() {
        this.remountInput();
        this.scheduleFocusRestore();

        await this.fetchContext();
        if (this.$route.query.order_id) {
            const opId = Number(this.$route.query.order_id);
            await this.loadActiveOrderDetails(opId);
            await this.fetchHistory(opId);
        } else if (this.$route.query.order && this.$route.query.operation) {
            this.state.activeContext = {
                order: `${this.$route.query.order}/${this.$route.query.operation_number || ""}`,
                operation: this.$route.query.operation,
                resource: this.$route.query.resource || "Leitor Manual",
                resource_id: this.$route.query.resource_id ? Number(this.$route.query.resource_id) : null,
                order_id: null,
                planned_quantity: 0,
                produced_quantity: 0,
                status_name: "Aguardando",
                status_color: "#738091"
            };
            this.setFeedback(`Ordem ${this.state.activeContext.order} carregada. Aguardando leitura do status.`);
            await this.fetchHistory();
        } else {
            await this.fetchHistory();
        }
        this.remountInput();
        this.scheduleFocusRestore();

        window.addEventListener("keydown", this.handleGlobalEscape);
        window.addEventListener("keydown", this.handleGlobalKeyDown, true);
        window.addEventListener("focus", this.handleWindowFocus);
        document.addEventListener("visibilitychange", this.handleVisibilityChange);

        // Persistent focus guard: re-focus scanInput every 1.5s if no modal/overlay is active
        this._focusGuardId = setInterval(() => {
            if (this.isLoading || this._anyModalOpen() || this.flagShowOverlay || this.flagShowModalPin) return;
            const scanEl = this.$refs.scanInput;
            if (!scanEl) return;
            const active = document.activeElement;
            const isInsideSwal = active && active.closest('.swal2-container');
            if (!isInsideSwal && active !== scanEl) {
                const tag = active ? active.tagName : '';
                if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
                    this.focusInput();
                }
            }
        }, 1500);
    },
    beforeDestroy() {
        window.removeEventListener("keydown", this.handleGlobalEscape);
        window.removeEventListener("keydown", this.handleGlobalKeyDown, true);
        window.removeEventListener("focus", this.handleWindowFocus);
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        if (this._focusGuardId) {
            clearInterval(this._focusGuardId);
            this._focusGuardId = null;
        }
        this._clearFocusTimers();
    },
    methods: {
        ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
        redirectPage(route) {
            const prefix = localStorage.getItem("prefix") || this.$route.params.prefix;
            this.$router.push({ path: `/${prefix}/${route}` });
        },
        goToBarcodePrint() {
            const prefix = localStorage.getItem("prefix") || this.$route.params.prefix;
            this.$router.push({
                name: "operator-barcode-status-print",
                params: { prefix },
                query: { ...this.$route.query }
            });
        },
        remountInput() {
            if (this.isLoading) return;
            this.inputKey++;
            this.scanInput = "";
            this.$nextTick(() => {
                this.focusInput();
            });
        },
        _anyModalOpen() {
            return !this.stopModalHidden || !this.quantityModalHidden || this.configModalOpen;
        },
        scheduleFocusRestore() {
            this._clearFocusTimers();
            const delays = [80, 250, 500, 900, 1400];
            this._pendingFocusTimers = delays.map(ms =>
                setTimeout(() => {
                    if (this.isLoading || this._anyModalOpen() || this.flagShowOverlay || this.flagShowModalPin) return;
                    const active = document.activeElement;
                    const isInsideSwal = active && active.closest('.swal2-container');
                    if (isInsideSwal) return;
                    if (ms >= 500) {
                        this.remountInput();
                    } else {
                        this.focusInput();
                    }
                }, ms)
            );
        },
        _clearFocusTimers() {
            if (this._pendingFocusTimers) {
                this._pendingFocusTimers.forEach(id => clearTimeout(id));
                this._pendingFocusTimers = null;
            }
        },
        handleWindowFocus() {
            if (this.isLoading || this._anyModalOpen() || this.flagShowOverlay || this.flagShowModalPin) return;
            this.remountInput();
        },
        handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                if (this.isLoading || this._anyModalOpen() || this.flagShowOverlay || this.flagShowModalPin) return;
                this.remountInput();
            }
        },
        handleGlobalKeyDown(event) {
            if (this.isLoading) {
                if (event.ctrlKey || event.altKey || event.metaKey) return;
                event.preventDefault();
                return;
            }
            // Do not intercept if any modal, overlay, PIN modal or SweetAlert is open
            if (this._anyModalOpen() || this.flagShowOverlay || this.flagShowModalPin) return;

            const active = document.activeElement;
            const isInsideSwal = active && active.closest('.swal2-container');
            if (isInsideSwal) return;

            const tag = active ? active.tagName : '';
            // If user is currently typing in another input element, don't hijack
            if (active !== this.$refs.scanInput && (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')) {
                return;
            }

            // Ignore modifier keys and navigation shortcuts
            if (event.ctrlKey || event.altKey || event.metaKey) return;
            if (['Escape', 'Tab', 'CapsLock', 'Shift', 'Control', 'Alt', 'Meta', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
                return;
            }

            const scanEl = this.$refs.scanInput;
            if (!scanEl) return;

            // If focus is not currently on scanInput, bring focus & cursor to it immediately
            if (document.activeElement !== scanEl) {
                this.focusInput();
            }
        },
        handleWorkspaceClick(event) {
            const tag = event.target ? event.target.tagName : '';
            if (['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'A', 'SVG', 'PATH'].includes(tag) || (event.target && event.target.closest('button'))) {
                return;
            }
            if (this._anyModalOpen() || this.flagShowOverlay || this.flagShowModalPin) return;
            const active = document.activeElement;
            if (active && active.closest('.swal2-container')) return;
            this.focusInput();
        },
        onScanInputFocus() {
            this.isInputFocused = true;
        },
        onScanInputBlur() {
            this.isInputFocused = false;
            setTimeout(() => {
                if (this.isLoading || this._anyModalOpen() || this.flagShowOverlay || this.flagShowModalPin) return;
                const active = document.activeElement;
                const isInsideSwal = active && active.closest('.swal2-container');
                if (isInsideSwal) return;
                const tag = active ? active.tagName : "";
                if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
                this.focusInput();
            }, 150);
        },
        focusInput() {
            this.$nextTick(() => {
                const scanEl = this.$refs.scanInput;
                if (scanEl) {
                    scanEl.focus();
                    const len = scanEl.value ? scanEl.value.length : 0;
                    try {
                        scanEl.setSelectionRange(len, len);
                    } catch (e) {}
                    this.isInputFocused = true;
                }
            });
        },
        focusAndSelect() {
            this.$nextTick(() => {
                const scanEl = this.$refs.scanInput;
                if (scanEl) {
                    scanEl.focus();
                    try {
                        scanEl.select();
                    } catch (e) {}
                    this.isInputFocused = true;
                }
            });
        },
        setFeedback(message, type = "") {
            this.feedback.message = message;
            this.feedback.type = type;
        },
        getStopCount() {
            return this.state.readings.filter((item) => item.statusCode === "99").length;
        },
        getProducedQuantity() {
            return this.state.readings
                .filter((item) => item.statusCode === "4")
                .reduce((total, item) => total + Number(item.quantity || 0), 0);
        },
        async fetchHistory(operationId = null) {
            try {
                let url = `/api/mysfc/operator/operation-progress?siteId=${this.currentSite}`;
                if (operationId) {
                    url += `&operationId=${operationId}`;
                }
                const response = await this.$http2.get(url);
                const data = response.data?.data || response.data || [];

                if (Array.isArray(data)) {
                    this.state.readings = data.map((item) => {
                        let statusCode = "4";
                        const sName = (item.statusName || "").toLowerCase();

                        if (sName.includes("não iniciado") || sName.includes("nao iniciado") || sName.includes("not initialized")) {
                            statusCode = "1";
                        } else if (sName.includes("setup")) {
                            statusCode = "2";
                        } else if (sName.includes("iniciado") || sName.includes("started") || sName.includes("start")) {
                            statusCode = "3";
                        } else if (sName.includes("finalizada") || sName.includes("completed") || sName.includes("finalizad")) {
                            statusCode = "5";
                        } else if (sName.includes("pausa") || sName.includes("stop") || sName.includes("parada") || item.refuseReasonId) {
                            statusCode = "99";
                        } else {
                            statusCode = "4";
                        }

                        const parsedDate = item.eventTime ? new Date(item.eventTime) : new Date();

                        return {
                            id: item.id || `${Date.now()}-${Math.random()}`,
                            statusCode,
                            order: item.orderNumber ? `${item.orderNumber}/${item.operationNumber || ""}` : (this.state.activeContext ? this.state.activeContext.order : "-"),
                            operation: item.name || item.operationName || (this.state.activeContext ? this.state.activeContext.operation : "Operação"),
                            quantity: item.producedQuantity || item.quantity || null,
                            quantitySource: "MANUAL",
                            reason: item.refuseReasonName || item.reason || item.note || "",
                            timestamp: parsedDate
                        };
                    });
                }
            } catch (error) {
                console.error("Erro ao carregar histórico de apontamentos:", error);
            }
        },
        async deleteReading(item) {
            const isPersisted = item.id && !String(item.id).includes("-");
            const label = this.STATUS[item.statusCode] ? this.STATUS[item.statusCode].label : "Apontamento";

            const confirm = await this.$swal({
                title: "Excluir apontamento?",
                text: `Deseja realmente remover o apontamento "${label}" da ordem ${item.order}?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Sim, excluir",
                cancelButtonText: "Cancelar",
                customClass: {
                    confirmButton: "btn btn-danger",
                    cancelButton: "btn btn-outline-secondary ml-1"
                },
                buttonsStyling: false,
                returnFocus: false
            });

            if (!confirm.value) {
                this.focusInput();
                return;
            }

            if (isPersisted) {
                this.isLoading = true;
                try {
                    await this.$http2.delete(`/api/mysfc/operation-progress/${item.id}?siteId=${this.currentSite}`);
                    this.showToast("Sucesso", "CheckIcon", "Apontamento excluído com sucesso.", "success");
                    this.setFeedback("Apontamento excluído com sucesso.", "success");

                    if (this.state.activeContext?.order_id) {
                        await this.loadActiveOrderDetails(this.state.activeContext.order_id);
                        await this.fetchHistory(this.state.activeContext.order_id);
                    } else {
                        await this.fetchHistory();
                    }
                } catch (error) {
                    console.error("Erro ao excluir apontamento:", error);
                    let detail = error.response?.data?.message || "Erro de comunicação com o servidor.";
                    this.showToast("Erro", "FrownIcon", `Falha ao excluir: ${detail}`, "danger");
                } finally {
                    this.isLoading = false;
                    this.remountInput();
                }
            } else {
                this.state.readings = this.state.readings.filter(r => r.id !== item.id);
                this.showToast("Sucesso", "CheckIcon", "Leitura local removida.", "success");
                this.remountInput();
            }
        },
        formatTime(date) {
            if (!date) return "";
            const d = date instanceof Date ? date : new Date(date);
            if (isNaN(d.getTime())) return "";
            return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
        },
        getEventDetail(item) {
            if (item.reason) {
                return `Motivo: ${item.reason}${item.note ? ` · ${item.note}` : ""}`;
            }
            if (item.quantity) {
                const sourceMap = {
                    DEFAULT: "quantidade padrão",
                    CODE: "informada no código",
                    MANUAL: "informada pelo operador"
                };
                return `Quantidade: ${item.quantity} peças · ${sourceMap[item.quantitySource] || ""}`;
            }
            return `Código ${item.statusCode}`;
        },
        showToast(title, icon, text, variant) {
            this.$toast({
                component: ToastificationContent,
                props: {
                    title,
                    icon,
                    text,
                    variant
                }
            });
        },
        adjustTimeZone3(inputDate) {
            let date = inputDate;
            if (!(inputDate instanceof Date)) {
                date = new Date(inputDate);
            }
            const offset = date.getTimezoneOffset() * 60000;
            const localISOTime = new Date(date.getTime() - offset).toISOString().slice(0, -1);
            return localISOTime;
        },
        async fetchContext() {
            try {
                const response = await this.$http2.get(`/api/mysfc/operation-progress/context?siteId=${this.currentSite}`);
                const data = response.data?.data || {};
                this.statusList = data.status?.map((e) => ({
                    ...e,
                    text: e.name || e.description || "",
                    value: e.id
                })) || [];
                this.rejectionReasons = this.statusList.filter(
                    (e) =>
                        (e.status_classification_id === 1 && (e.name !== "Setup" || e.value === 2)) ||
                        e.status_classification_id === 2 ||
                        e.status_classification_id === 3
                );
            } catch (error) {
                console.error("Erro ao carregar contexto de apontamento:", error);
                this.showToast("Erro", "FrownIcon", "Erro ao carregar os motivos de parada e status da API.", "danger");
            }
        },
        resolveStatusId(statusText) {
            const normalized = statusText ? statusText.toLowerCase().trim() : "";
            if (!this.statusList || this.statusList.length === 0) return null;
            
            let match = this.statusList.find(s => {
                const name = (s.name || s.text || s.description || "").toLowerCase().trim();
                if (normalized === 'setup') {
                    return name === 'setup' || name.includes('setup');
                }
                if (normalized === 'iniciado') {
                    return (name.includes('iniciad') || name.includes('start')) && !name.includes('não') && !name.includes('nao') && !name.includes('not');
                }
                if (normalized === 'executando') {
                    return name.includes('execut') || name.includes('running') || name.includes('produç') || name.includes('produc');
                }
                if (normalized === 'finalizada') {
                    return name.includes('finalizad') || name.includes('complete');
                }
                if (normalized === 'pausa') {
                    return name.includes('pausa') || name.includes('stop') || name.includes('parada');
                }
                return false;
            });
            
            return match ? match.value : null;
        },
        async loadActiveOrderDetails(orderId) {
            try {
                const response = await this.$http2.get(`/api/mysfc/operator/operation/${orderId}?siteId=${this.currentSite}`);
                const data = response.data?.data || response.data;
                if (data) {
                    const statusName = data.lastInfo?.lastStatus || data.lastByUser?.lastStatusByUser || "Não Iniciado";
                    const statusColor = data.lastInfo?.lastColorStatus || data.lastByUser?.lastColorStatusByUser || "#61b3fa";

                    this.state.activeContext = {
                        order: `${data.orderNumber || this.$route.query.order}/${data.operationNumber || this.$route.query.operation_number || ""}`,
                        operation: data.operationName || this.$route.query.operation,
                        resource: data.resources?.[0]?.name || this.$route.query.resource || "Leitor Manual",
                        resource_id: data.resources?.[0]?.id || this.$route.query.resource_id || null,
                        order_id: data.id || orderId,
                        planned_quantity: data.plannedQuantity || 0,
                        produced_quantity: data.producedQuantity || 0,
                        status_name: statusName,
                        status_color: statusColor
                    };
                    this.setFeedback(`Ordem ${this.state.activeContext.order} carregada. Status atual: ${this.state.activeContext.status_name}.`, "success");
                }
            } catch (error) {
                console.error("Erro ao carregar detalhes da ordem:", error);
                this.showToast("Erro", "FrownIcon", "Erro ao carregar detalhes da ordem da API.", "danger");
            }
        },
        onScanKeydown(event) {
            if (this.isLoading) {
                if (event.key !== "Tab") event.preventDefault();
                return;
            }
            if (event.key === "Enter") {
                this.processInput();
            }
        },
        processInput() {
            if (this.isLoading || !this.scanInput) return;
            this.processReading(this.scanInput);
        },
        normalizeScanPayload(raw) {
            return String(raw || "")
                .replace(/[\r\n\t]/g, "")
                .replace(/[çÇ]/g, ";")
                .trim();
        },
        async processReading(raw) {
            if (this.isLoading) return;
            const normalized = this.normalizeScanPayload(raw);
            if (!normalized) return;

            const formatHint = "Use o formato ORDEM;OPERAÇÃO;QUANTIDADE, por exemplo A001;10;50.";
            const statusHint = "Status não reconhecido. Use STATUS;000002, STATUS;000003, STATUS;000004, STATUS;000005 ou STATUS;000099.";

            // Pattern: Production scan "ORDEM/OPERAÇÃO/QTDE" (also accepts ; and |)
            if (/[;|/]/.test(normalized)) {
                const parts = normalized.split(/[;|/]/).map((part) => part.trim());
                if (parts.length === 3) {
                    const [orderNum, opNum, quantityText] = parts;
                    const quantity = Number(quantityText);
                    if (!orderNum || !opNum || isNaN(quantity) || quantity <= 0) {
                        this.setFeedback(formatHint, "error");
                        this.focusAndSelect();
                        return;
                    }

                    // Check if an order is currently active and validate matching order & operation
                    if (this.state.activeContext && this.state.activeContext.order) {
                        const orderParts = String(this.state.activeContext.order).split("/");
                        const activeOrderNum = orderParts[0] ? orderParts[0].trim() : "";
                        const activeOpNum = orderParts[1] ? orderParts[1].trim() : "";

                        if (activeOrderNum && orderNum.toUpperCase() !== activeOrderNum.toUpperCase()) {
                            const errorMsg = `A etiqueta lida (Ordem ${orderNum}) pertence a uma ordem diferente da selecionada (Ordem ${activeOrderNum}).`;
                            this.setFeedback(errorMsg, "error");
                            this.showToast("Ordem Incompatível", "FrownIcon", errorMsg, "danger");
                            this.focusAndSelect();
                            return;
                        }

                        if (activeOpNum && parseInt(opNum) !== parseInt(activeOpNum)) {
                            const errorMsg = `A etiqueta lida (Operação ${opNum}) pertence a uma operação diferente da selecionada (Operação ${activeOpNum}).`;
                            this.setFeedback(errorMsg, "error");
                            this.showToast("Operação Incompatível", "FrownIcon", errorMsg, "danger");
                            this.focusAndSelect();
                            return;
                        }
                    }

                    this.isLoading = true;
                    try {
                        const response = await this.$http2.get(`/api/mysfc/operator/order?siteId=${this.currentSite}&completedOrders=false&filter=${orderNum}`);
                        const operations = response.data?.data || [];
                        const opNumberInt = parseInt(opNum);
                        const matched = operations.find(o => o.operation?.number === opNumberInt);

                        if (!matched) {
                            this.setFeedback(`Operação ${opNum} da Ordem ${orderNum} não encontrada.`, "error");
                            this.showToast("Erro", "FrownIcon", `Operação ${opNum} da Ordem ${orderNum} não encontrada ou não permitida.`, "danger");
                            return;
                        }

                        // Populate active context
                        this.state.activeContext = {
                            order: `${matched.order?.number || orderNum}/${matched.operation?.number || opNum}`,
                            operation: matched.operation?.name || "Operação por código de barras",
                            resource: matched.resource?.[0]?.name || "Leitor Manual",
                            resource_id: matched.resource?.[0]?.id || null,
                            order_id: matched.operation?.id,
                            planned_quantity: matched.order?.quantity || 0,
                            produced_quantity: matched.operation?.producedQuantity || 0,
                            status_name: matched.operation?.statusName || "Não Iniciado",
                            status_color: matched.operation?.statusColor || "#61b3fa"
                        };

                        // Now commit production appointment
                        const statusId = this.resolveStatusId("executando");
                        await this.commitReadingReal({
                            statusCode: "4",
                            statusId,
                            order: this.state.activeContext.order,
                            quantity,
                            quantitySource: "CODE"
                        });

                    } catch (err) {
                        console.error("Erro ao buscar ordem pelo código de barras:", err);
                        this.showToast("Erro", "FrownIcon", "Erro ao comunicar com o servidor.", "danger");
                    } finally {
                        this.isLoading = false;
                        this.remountInput();
                    }
                    return;
                }

                const prefixedStatus = this.resolveScannedStatusCode(normalized);
                if (prefixedStatus) {
                    this.handleStatusReading(prefixedStatus);
                    return;
                }

                this.setFeedback(formatHint, "error");
                this.focusAndSelect();
                return;
            }

            const statusCode = this.resolveScannedStatusCode(normalized);
            if (!statusCode) {
                this.setFeedback(statusHint, "error");
                this.focusAndSelect();
                return;
            }

            this.handleStatusReading(statusCode);
        },
        resolveScannedStatusCode(normalized) {
            if (!normalized) return null;
            const compact = String(normalized).toUpperCase().replace(/\s+/g, "").replace(/[Ç]/g, ";");
            const prefixed = compact.match(/^(?:STATUS|ST|S)[;|/]?(\d+)$/);
            const code = prefixed ? String(parseInt(prefixed[1], 10)) : compact;
            return this.STATUS[code] ? code : null;
        },
        handleStatusReading(statusCode) {
            if (!this.state.activeContext?.order_id) {
                this.setFeedback("Nenhuma ordem ativa. Selecione uma na listagem de ordens ou leia uma string de produção (ex: A001;10;50).", "error");
                this.focusAndSelect();
                return;
            }

            const reading = {
                order: this.state.activeContext.order,
                statusCode
            };

            if (statusCode === "99") {
                this.openStopModal(reading);
                return;
            }

            if (statusCode === "4") {
                if (this.useDefaultQuantity && Number.isInteger(this.defaultQuantity) && this.defaultQuantity > 0) {
                    const statusId = this.resolveStatusId("executando");
                    this.commitReadingReal(reading, {
                        statusId,
                        quantity: this.defaultQuantity,
                        quantitySource: "DEFAULT"
                    });
                    return;
                }
                if (this.useDefaultQuantity) {
                    this.setFeedback("Informe uma quantidade padrão inteira maior que zero. Abra as configurações.", "error");
                    this.openConfigModal();
                    return;
                }
                this.openQuantityModal(reading);
                return;
            }

            let statusText = "";
            if (statusCode === "2") statusText = "setup";
            else if (statusCode === "3") statusText = "iniciado";
            else if (statusCode === "5") statusText = "finalizada";

            const statusId = this.resolveStatusId(statusText);
            this.commitReadingReal(reading, {
                statusId
            });
        },
        async commitReadingReal(reading, additions = null) {
            this.isLoading = true;
            const merged = { ...reading, ...additions };
            const statusLabel = this.STATUS[merged.statusCode].label;
            const statusColor = this.STATUS[merged.statusCode].color;

            try {
                // Post appointment to API
                const body = {
                    operationId: this.state.activeContext.order_id,
                    userGuid: this.$cookies.get("userInfo").userData.guid,
                    statusId: merged.statusId,
                    producedQuantity: merged.quantity || null,
                    refuse: 0,
                    rework: 0,
                    resourceId: this.state.activeContext.resource_id,
                    eventTime: this.adjustTimeZone3(new Date()),
                    refuseReasonId: merged.refuseReasonId || null,
                    isRework: false,
                    industrialParameter: []
                };

                await this.$http2.post(`/api/mysfc/operator/operation-progress?siteId=${this.currentSite}`, body);

                // On success, re-fetch database history and order details
                if (this.state.activeContext?.order_id) {
                    await this.loadActiveOrderDetails(this.state.activeContext.order_id);
                    await this.fetchHistory(this.state.activeContext.order_id);
                } else {
                    await this.fetchHistory();
                }

                const suffix = merged.reason
                    ? ` - ${merged.reason}`
                    : merged.quantity
                        ? ` - ${merged.quantity} peças`
                        : "";

                this.setFeedback(`${statusLabel} registrado com sucesso para a ordem ${merged.order}${suffix}.`, "success");
                this.showToast("Sucesso", "CheckIcon", `${statusLabel} apontado com sucesso.`, "success");

                this.scanInput = "";
            } catch (error) {
                console.error("Erro ao realizar apontamento no backend:", error);

                let detail = "";
                if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                    detail = error.response.data.errors.map(e => e.message).join(", ");
                } else if (error.response?.data?.message) {
                    detail = error.response.data.message;
                }

                this.setFeedback(`Erro ao apontar ${statusLabel}: ${detail || error.message}`, "error");
                this.showToast("Erro", "FrownIcon", `Erro ao apontar ${statusLabel}: ${detail || "comunicação com a API falhou"}`, "danger");
            } finally {
                this.isLoading = false;
                this.remountInput();
            }
        },
        simulateStatus(code) {
            if (this.isLoading) return;
            this.scanInput = code;
            this.processReading(code);
        },
        resetSimulation() {
            this.state.readings = [];
            this.setFeedback("Simulação limpa. Aguardando nova leitura.");
            this.focusInput();
        },
        // Modals management
        openQuantityModal(reading) {
            this.state.pendingProduction = reading;
            this.productionQuantity = "";
            this.productionQuantityError = false;
            this.quantityModalHidden = false;
            document.body.style.overflow = "hidden";
            this.$nextTick(() => {
                if (this.$refs.productionQuantityInput) {
                    this.$refs.productionQuantityInput.focus();
                }
            });
            this.setFeedback(`Produção identificada para ${reading.order}. Informe a quantidade para gravar.`);
        },
        closeQuantityModal() {
            this.quantityModalHidden = true;
            document.body.style.overflow = "";
            this.state.pendingProduction = null;
            this.scanInput = "";
            this.setFeedback("Leitura de produção cancelada; nenhum evento foi gravado.");
            this.focusInput();
        },
        async submitQuantity() {
            const quantity = Number(this.productionQuantity);
            const valid = Number.isInteger(quantity) && quantity > 0;
            this.productionQuantityError = !valid;
            if (!valid) return;

            const pending = this.state.pendingProduction;
            this.quantityModalHidden = true;
            document.body.style.overflow = "";
            this.state.pendingProduction = null;

            const statusId = this.resolveStatusId("executando");
            await this.commitReadingReal(pending, {
                statusId,
                quantity,
                quantitySource: "MANUAL"
            });
        },
        openStopModal(reading) {
            this.state.pendingStop = reading;
            this.stopReason = "";
            this.otherReason = "";
            this.reasonError = false;
            this.otherReasonError = false;
            this.stopNote = "";
            this.stopModalHidden = false;
            document.body.style.overflow = "hidden";
            this.setFeedback(`Parada identificada para ${reading.order}. Informe o motivo para gravar.`);
        },
        closeStopModal() {
            this.stopModalHidden = true;
            document.body.style.overflow = "";
            this.state.pendingStop = null;
            this.scanInput = "";
            this.setFeedback("Leitura de parada cancelada; nenhum evento foi gravado.");
            this.focusInput();
        },
        async submitStop() {
            const reason = this.stopReason;
            const customReason = this.otherReason.trim();
            this.reasonError = !reason;
            this.otherReasonError = reason === "Outro" && !customReason;

            if (!reason || (reason === "Outro" && !customReason)) return;

            const pending = this.state.pendingStop;
            this.stopModalHidden = true;
            document.body.style.overflow = "";
            this.state.pendingStop = null;

            const statusId = reason === "Outro" ? this.resolveStatusId("pausa") : Number(reason);
            const reasonName = reason === "Outro"
                ? customReason
                : this.rejectionReasons.find(r => r.value === Number(reason))?.text || "";

            await this.commitReadingReal(pending, {
                statusCode: "99",
                statusId,
                refuseReasonId: null,
                reason: reasonName,
                note: this.stopNote.trim()
            });
        },
        handleGlobalEscape(event) {
            if (event.key !== "Escape") return;
            if (this.configModalOpen) this.closeConfigModal();
            else if (!this.stopModalHidden) this.closeStopModal();
            else if (!this.quantityModalHidden) this.closeQuantityModal();
        },
        openConfigModal() {
            this.configModalOpen = true;
            document.body.style.overflow = "hidden";
            this.$nextTick(() => {
                if (this.$refs.configQuantityInput && this.useDefaultQuantity) {
                    this.$refs.configQuantityInput.focus();
                    this.$refs.configQuantityInput.select();
                }
            });
        },
        closeConfigModal() {
            this.configModalOpen = false;
            document.body.style.overflow = "";
            this.focusInput();
        }
    }
};
</script>

<style lang="scss">
.app-content-operator:has(#barcode-status-appointment) {
    padding: 0 !important;
}
</style>

<style lang="scss" scoped>
#barcode-status-appointment {
    --orange: #ea7100;
    --orange-dark: #b85f00;
    --ink: #18212f;
    --muted: #738091;
    --line: #dfe3e8;
    --surface: #ffffff;
    --canvas: #f5f6f7;
    --setup: #737d8d;
    --started: #2864dc;
    --production: #16a34a;
    --finished: #7c3aed;
    --stopped: #e5252a;
    --shadow: 0 18px 45px rgba(27, 34, 44, .13);

    color: var(--ink);
    width: 100%;
    min-height: calc(100vh - 120px);
    font-family: Inter, "Segoe UI", Arial, sans-serif;
    display: flex;
    flex-direction: column;

    * {
        box-sizing: border-box;
    }

    .header-operator {
        width: 100%;
        border-top: 1px solid #cfc4be;
        border-bottom: 1px solid #cfc4be;
        padding: 15.5px 29px;
        z-index: 1;
        gap: 16px;
        display: flex;
        align-items: center;
        background: #ffffff;
        color: #4c4541;
        box-shadow: 0px 8px 30px rgba(58, 46, 38, 0.1);

        .header__back {
            display: flex;
            cursor: pointer;
        }

        .header__title {
            font-family: "Raleway";
            font-style: normal;
            font-weight: 600;
            font-size: 22px;
            line-height: 30px;
        }

        .header__action {
            margin-left: auto;
            border: 1px solid #ea7100;
            background: #ea7100;
            color: #fff;
            border-radius: 8px;
            padding: 8px 14px;
            font-weight: 700;
            font-size: 14px;
            line-height: 20px;
            cursor: pointer;
            white-space: nowrap;
        }

        @media (max-width: 560px) {
            flex-wrap: wrap;

            .header__action {
                width: 100%;
                margin-left: 0;
            }
        }
    }

    .body-content {
        width: 100%;
        padding: 16px 29px 24px;

        @media (max-width: 900px) {
            padding: 16px 16px 24px;
        }

        @media (max-width: 560px) {
            padding: 12px 10px 16px;
        }
    }
}

.prototype-badge {
    padding: 4px 9px;
    border-radius: 20px;
    background: #fff0dc;
    color: #9c5200;
    font-size: 11px;
    font-weight: 700;
}

.info-card,
.scanner-card,
.history-panel {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 14px;
}

.info-card {
    overflow: hidden;
    margin-bottom: 18px;

    h2 {
        font-size: 12px;
        color: #5f6977;
        font-weight: 600;
        margin: 0;
        padding: 10px 16px;
        border-bottom: 1px solid var(--line);
    }
}

.metrics {
    display: grid;
    grid-template-columns: repeat(5, 1fr);

    div {
        min-height: 72px;
        display: grid;
        place-content: center;
        text-align: center;
        border-right: 1px solid var(--line);
        padding: 10px;

        &:last-child {
            border-right: 0;
        }
    }

    span {
        font-size: 10px;
        color: #8b929c;
        margin-bottom: 7px;
    }

    strong {
        font-size: 15px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 220px;
    }
}

.workspace {
    display: grid;
    grid-template-columns: minmax(310px, 390px) 1fr;
    gap: 18px;
    align-items: start;
}

.scanner-card {
    padding: 26px 24px 22px;
    text-align: center;
    position: sticky;
    top: 16px;

    >h2 {
        font-size: 17px;
        margin: 0 0 9px;
    }

    >p {
        margin: 0 0 22px;
        color: #88909b;
        font-size: 12px;
        line-height: 1.55;
    }

    label {
        display: block;
        text-align: left;
        font-size: 12px;
        font-weight: 700;
        margin-bottom: 7px;
    }
}

.scanner-illustration {
    height: 47px;
    display: flex;
    gap: 5px;
    align-items: stretch;
    justify-content: center;
    margin: 1px 0 14px;

    i {
        display: block;
        width: 4px;
        background: var(--orange);
        border-radius: 2px;

        &:nth-child(2) {
            width: 7px;
        }

        &:nth-child(4) {
            width: 2px;
        }
    }
}

code {
    background: #f4f4f5;
    color: #7c3d00;
    border-radius: 4px;
    padding: 1px 4px;
}

.active-context {
    display: grid;
    grid-template-columns: 1fr 1.3fr;
    text-align: left;
    margin: -7px 0 17px;
    border: 1px solid #e0e4e8;
    border-radius: 9px;
    overflow: hidden;
    background: #fafbfb;

    div {
        padding: 10px 11px;
        min-width: 0;

        +div {
            border-left: 1px solid #e0e4e8;
        }
    }

    span {
        display: block;
        color: #8a929d;
        font-size: 9px;
        margin-bottom: 4px;
    }

    strong {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 12px;
    }
}

.input-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 7px;

    label {
        margin-bottom: 0 !important;
    }
}

.scanner-status-pill {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
    cursor: pointer;
    transition: all .2s ease;
    user-select: none;

    &.active {
        background: #e6f4ea;
        color: #137333;
        border: 1px solid #ceead6;
    }

    &.inactive {
        background: #fef7e0;
        color: #b06000;
        border: 1px solid #feefc3;
    }

    &.processing {
        background: #fff4e5;
        color: #b45309;
        border: 1px solid #fed7aa;
        cursor: default;
    }
}

.input-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;

    input {
        width: 100%;
        border: 1px solid #cfd5dc;
        background: #fff;
        border-radius: 8px;
        padding: 10px 11px;
        color: var(--ink);
        outline: none;
        caret-color: #ea7100 !important;
        user-select: text !important;
        -webkit-user-select: text !important;

        &:focus {
            border-color: var(--orange);
            box-shadow: 0 0 0 3px rgba(234, 113, 0, .13);
            caret-color: #ea7100 !important;
        }

        &[readonly] {
            background: #f6f7f9;
            caret-color: transparent !important;
            cursor: wait;

            &:focus {
                border-color: #d0d5dd;
                box-shadow: none;
                caret-color: transparent !important;
            }
        }
    }
}

.primary-button,
.secondary-button,
.danger-button,
.production-button {
    border-radius: 8px;
    padding: 10px 15px;
    font-weight: 700;
    border: 1px solid transparent;
}

.primary-button {
    background: var(--orange);
    color: #fff;

    &:hover {
        background: var(--orange-dark);
    }

    &:disabled {
        opacity: 0.65;
        cursor: wait;
    }
}

.secondary-button {
    background: #fff;
    color: #596371;
    border-color: #ccd2d9;

    &:hover {
        background: #f6f7f8;
    }
}

.danger-button {
    background: var(--stopped);
    color: #fff;

    &:hover {
        background: #c9191e;
    }
}

.production-button {
    background: var(--production);
    color: #fff;

    &:hover {
        background: #12833d;
    }
}

.feedback {
    min-height: 32px;
    text-align: left;
    font-size: 11px;
    color: #76808c;
    padding-top: 8px;

    &.success {
        color: #13803c;
    }

    &.error {
        color: #c91f24;
    }
}

/* Quantity Config Summary (inline compact display) */
.quantity-config-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 14px;
    padding: 10px 13px;
    border: 1px solid #dce8df;
    border-radius: 9px;
    background: #f7fbf8;
    text-align: left;
}

.quantity-config-summary__info {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.quantity-config-summary__label {
    color: #6b7685;
    font-size: 11px;
    white-space: nowrap;
}

.quantity-config-summary__value {
    font-size: 12px;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.quantity-config-summary__btn {
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 1px solid #cdd4db;
    border-radius: 7px;
    background: #fff;
    color: #5f6977;
    cursor: pointer;
    transition: all .18s;

    &:hover {
        background: #f0f2f4;
        color: var(--orange);
        border-color: var(--orange);
    }
}

/* Config Modal specific styles */
.config-custom-modal .modal-accent,
.config-accent {
    background: var(--orange) !important;
}

.config-kicker {
    color: var(--orange) !important;
}

.config-symbol {
    width: 45px;
    height: 45px;
    display: grid;
    place-items: center;
    background: #fff5eb;
    color: var(--orange);
    border-radius: 50%;
    border: 2px solid #fcd9b5;
}

.config-modal-body {
    margin-top: 16px;
    padding: 16px;
    border: 1px solid #dce8df;
    border-radius: 10px;
    background: #f7fbf8;
    text-align: left;

    h3 {
        margin: 0 0 3px;
        font-size: 13px;
    }

    .config-desc {
        margin: 0 0 14px;
        color: var(--muted);
        font-size: 11px;
    }

    >label {
        margin-top: 2px;
        display: block;
        font-size: 12px;
        font-weight: 700;
        margin-bottom: 7px;
    }

    #configDefaultQuantity:disabled {
        opacity: .5;
    }
}

.quantity-config-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.switch {
    position: relative;
    flex: 0 0 auto;
    margin: 0 !important;

    input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
    }

    span {
        width: 42px;
        height: 24px;
        display: block;
        border-radius: 20px;
        background: #b7bec7;
        transition: .18s;
        position: relative;

        &::after {
            content: "";
            position: absolute;
            width: 18px;
            height: 18px;
            left: 3px;
            top: 3px;
            border-radius: 50%;
            background: #fff;
            box-shadow: 0 1px 4px rgba(0, 0, 0, .22);
            transition: .18s;
        }
    }

    input:checked+span {
        background: var(--production);

        &::after {
            transform: translateX(18px);
        }
    }

    input:focus-visible+span {
        outline: 3px solid rgba(22, 163, 74, .25);
        outline-offset: 2px;
    }
}

.quantity-input {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;

    input {
        width: 100%;
        border: 1px solid #cfd5dc;
        background: #fff;
        border-radius: 8px 0 0 8px;
        padding: 10px 11px;
        color: var(--ink);
        outline: none;

        &:focus {
            border-color: var(--orange);
            box-shadow: 0 0 0 3px rgba(234, 113, 0, .13);
        }
    }

    span {
        align-self: stretch;
        display: grid;
        place-items: center;
        padding: 0 12px;
        border: 1px solid #cfd5dc;
        border-left: 0;
        border-radius: 0 8px 8px 0;
        background: #eef1f3;
        color: #69727e;
        font-size: 12px;
    }
}

.config-hint {
    margin-top: 7px;
    color: #40724f;
    font-size: 10.5px;
    line-height: 1.35;
}

.simulator {
    margin-top: 18px;
    padding-top: 17px;
    border-top: 1px solid var(--line);
    text-align: left;

    h3 {
        font-size: 12px;
        margin: 0 0 4px;
    }

    p {
        font-size: 11px;
        color: var(--muted);
        margin: 0 0 10px;
    }
}

.status-buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 7px;
}

.status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    border: 0;
    border-radius: 8px;
    color: #fff;
    padding: 9px 10px;
    font-weight: 700;
    font-size: 12px;
    cursor: pointer;

    &:disabled {
        opacity: 0.55;
        cursor: wait;
    }

    small {
        display: grid;
        place-items: center;
        min-width: 22px;
        height: 22px;
        background: rgba(255, 255, 255, .2);
        border-radius: 11px;
    }

    &.setup {
        background: var(--setup);
    }

    &.started {
        background: var(--started);
    }

    &.production {
        background: var(--production);
    }

    &.finished {
        background: var(--finished);
    }

    &.stopped {
        background: var(--stopped);
        grid-column: 1 / -1;
    }

    &:hover {
        filter: brightness(.93);
        transform: translateY(-1px);
    }
}

.history-panel {
    min-height: 530px;
    overflow: hidden;
}

.history-heading {
    padding: 18px 20px;
    border-bottom: 1px solid var(--line);
    display: flex;
    align-items: center;
    justify-content: space-between;

    h2 {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
    }
}

.eyebrow {
    margin: 0 0 3px;
    color: var(--orange);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .07em;
}

.count-pill {
    background: #f1f3f5;
    color: #6f7884;
    border-radius: 20px;
    padding: 6px 10px;
    font-size: 11px;
}

.history-list {
    padding: 12px;
    display: grid;
    gap: 9px;
}

.empty-state {
    min-height: 390px;
    display: grid;
    place-content: center;
    justify-items: center;
    color: #88919d;
    gap: 7px;

    strong {
        color: #555f6c;
    }

    span {
        font-size: 12px;
    }
}

.empty-icon {
    width: 54px;
    height: 54px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #f2f3f5;
    font-size: 26px;
    color: #b1b7bf;
}

.history-item {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    align-items: center;
    gap: 13px;
    border: 1px solid var(--line);
    border-radius: 11px;
    padding: 13px 15px;
    overflow: hidden;
    animation: enter .24s ease-out;

    ::before {
        content: "";
        position: absolute;
        inset: 0 auto 0 0;
        width: 4px;
        background: var(--status-color);
    }
}

.history-badge {
    color: #fff;
    background: var(--status-color);
    padding: 7px 9px;
    border-radius: 7px;
    font-weight: 700;
    font-size: 12px;
    min-width: 82px;
    text-align: center;
}

.history-main {
    strong {
        display: block;
        font-size: 15px;
        margin-bottom: 3px;
    }

    span {
        color: #7b8490;
        font-size: 12px;
    }
}

.history-time {
    text-align: right;
    color: #89919b;
    font-size: 11px;

    strong {
        display: block;
        color: var(--status-color);
        font-size: 11px;
        margin-bottom: 3px;
    }
}

.delete-reading-btn {
    border: none;
    background: transparent;
    color: #ea5455;
    padding: 6px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all .2s ease;
    margin-left: 2px;

    &:hover {
        background: #fee2e2;
        color: #dc2626;
    }
}

@keyframes enter {
    from {
        transform: translateY(-8px);
        opacity: 0;
    }
}

/* Modals */
.custom-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(18, 24, 33, .58);
    display: grid;
    place-items: center;
    padding: 18px;
    backdrop-filter: blur(2px);
}

.custom-modal {
    width: min(560px, 100%);
    max-height: calc(100vh - 36px);
    overflow: auto;
    background: #fff;
    border-radius: 15px;
    box-shadow: var(--shadow);
    position: relative;
    padding: 25px 28px 27px;
    display: block;

    p {
        color: #6f7986;
        font-size: 13px;
        line-height: 1.5;
        margin-top: 12px;
    }

    label {
        display: block;
        text-align: left;
        font-size: 12px;
        font-weight: 700;
        margin-bottom: 7px;

        span {
            color: #8a929c;
            font-weight: 400;
        }
    }
}

.modal-accent {
    position: absolute;
    inset: 0 0 auto;
    height: 5px;
    background: var(--stopped);
}

.production-custom-modal .modal-accent {
    background: var(--production);
}

.modal-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 13px;
    border-bottom: 0;
    padding: 0;

    h2 {
        font-size: 20px;
        margin: 3px 0 0;
        font-weight: 700;
    }
}

.stop-symbol {
    width: 45px;
    height: 45px;
    display: grid;
    place-items: center;
    background: #fff0f0;
    color: var(--stopped);
    border-radius: 50%;
    border: 2px solid #ffc8ca;
    font-weight: 900;
}

.quantity-symbol {
    width: 45px;
    height: 45px;
    display: grid;
    place-items: center;
    background: #edfaf1;
    color: var(--production);
    border-radius: 50%;
    border: 2px solid #bce8c9;
    font-weight: 900;
    font-size: 13px;
}

.modal-kicker {
    font-size: 10px;
    color: var(--stopped);
    font-weight: 800;
    letter-spacing: .08em;
}

.production-custom-modal .modal-kicker {
    color: var(--production);
}

.modal-quantity-input input {
    font-size: 20px;
    font-weight: 700;
}

.close-button {
    border: 0;
    background: transparent;
    color: #808895;
    font-size: 25px;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
        background: #f2f3f5;
    }
}

.order-summary {
    display: flex;
    justify-content: space-between;
    padding: 12px 14px;
    background: #f6f7f8;
    border-radius: 8px;
    margin: 15px 0 18px;

    span {
        color: #737d89;
        font-size: 12px;
    }

    strong {
        font-size: 13px;
    }
}

.field-error {
    margin: 5px 0 0;
    color: #c91f24 !important;
    font-size: 11px !important;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    margin-top: 22px;
}

select,
textarea {
    width: 100%;
    border: 1px solid #cfd5dc;
    background: #fff;
    border-radius: 8px;
    padding: 10px 11px;
    color: var(--ink);
    outline: none;

    &:focus {
        border-color: var(--orange);
        box-shadow: 0 0 0 3px rgba(234, 113, 0, .13);
    }
}

textarea {
    resize: vertical;
}

@media (max-width: 900px) {
    .workspace {
        grid-template-columns: 1fr;
    }

    .scanner-card {
        position: static;
    }

    .metrics {
        grid-template-columns: repeat(2, 1fr);

        div {
            border-bottom: 1px solid var(--line);

            &:last-child {
                grid-column: 1 / -1;
            }
        }
    }
}

@media (max-width: 560px) {
    .metrics {
        grid-template-columns: 1fr;
    }

    .scanner-card {
        padding: 21px 16px;
    }

    .header__action {
        width: 100%;
        margin-left: 0;
    }

    .input-row {
        grid-template-columns: 1fr;
    }

    .history-item {
        grid-template-columns: 1fr;
    }

    .history-time {
        text-align: left;
    }

    .history-badge {
        width: max-content;
    }

    .modal {
        padding: 20px 19px;
    }

    .modal-actions {
        flex-direction: column-reverse;

        button {
            width: 100%;
        }
    }
}
</style>
